import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { FileUp, FolderSearch, Play, Search } from 'lucide-react';
import { apiError } from '../api/client';
import {
  ImportacaoXmlResumo,
  importarXmlUpload,
  importarXmlPasta,
} from '../api/importacoes';
import LoadingSpinner from '../components/LoadingSpinner';

function Resumo({ data }: { data: ImportacaoXmlResumo | null }) {
  if (!data) return null;
  return (
    <section className="card-surface p-4 space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <Metric label="Arquivos" value={data.total_arquivos} />
        <Metric label="OK" value={data.sucesso} />
        <Metric label="Erros" value={data.erros} tone={data.erros ? 'text-rose-700 dark:text-rose-300' : undefined} />
        <Metric label={data.dry_run ? 'Criaria' : 'Criados'} value={data.criados} />
        <Metric label={data.dry_run ? 'Atualizaria' : 'Atualizados'} value={data.atualizados} />
        <Metric label="Produtos" value={data.produtos_criados} />
      </div>
      <div className="overflow-x-auto rounded-md border border-slate-200 dark:border-white/10">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-100 text-neutral-500 dark:bg-white/[0.04] dark:text-neutral-400">
            <tr>
              <th className="text-left px-3 py-2">Ação</th>
              <th className="text-left px-3 py-2">Documento</th>
              <th className="text-left px-3 py-2">Chave</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-left px-3 py-2">Itens</th>
              <th className="text-left px-3 py-2">Arquivo</th>
              <th className="text-left px-3 py-2">Mensagem</th>
            </tr>
          </thead>
          <tbody>
            {data.resultados.map((r, idx) => (
              <tr key={idx} className="border-t border-slate-100 hover:bg-slate-50 dark:border-white/5 dark:hover:bg-white/[0.02]">
                <td className={r.sucesso ? 'px-3 py-2 text-emerald-700 dark:text-emerald-300' : 'px-3 py-2 text-rose-700 dark:text-rose-300'}>{r.acao}</td>
                <td className="px-3 py-2">{r.numero ? `${r.numero}/${r.serie || 1}` : '-'}</td>
                <td className="px-3 py-2 font-mono text-xs">{r.chave || '-'}</td>
                <td className="px-3 py-2">{r.status || '-'}</td>
                <td className="px-3 py-2">{r.itens ?? '-'}</td>
                <td className="px-3 py-2 max-w-xs truncate" title={r.arquivo || ''}>{r.arquivo || '-'}</td>
                <td className="px-3 py-2 max-w-xs truncate" title={r.mensagem || ''}>{r.mensagem || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Metric({ label, value, tone }: { label: string; value: number; tone?: string }) {
  return (
    <div className="rounded-md border border-slate-200 bg-white px-3 py-2 shadow-sm dark:border-white/10 dark:bg-transparent dark:shadow-none">
      <div className="text-xs text-neutral-500 dark:text-neutral-400">{label}</div>
      <div className={`text-lg font-semibold text-slate-950 dark:text-slate-100 ${tone || ''}`}>{value}</div>
    </div>
  );
}

export default function ImportacoesXmlPage() {
  const [files, setFiles] = useState<File[]>([]);
  const [pasta, setPasta] = useState('/var/lib/emissorfiscal/documentos');
  const [limite, setLimite] = useState(200);
  const [resultado, setResultado] = useState<ImportacaoXmlResumo | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const uploadMut = useMutation({
    mutationFn: (dryRun: boolean) => importarXmlUpload(files, dryRun),
    onSuccess: (data) => { setResultado(data); setErro(null); },
    onError: (err) => setErro(apiError(err, 'Falha ao importar XML')),
  });

  const pastaMut = useMutation({
    mutationFn: (dryRun: boolean) => importarXmlPasta({ pasta, limite, dry_run: dryRun }),
    onSuccess: (data) => { setResultado(data); setErro(null); },
    onError: (err) => setErro(apiError(err, 'Falha ao varrer pasta')),
  });

  const busy = uploadMut.isPending || pastaMut.isPending;

  return (
    <div className="space-y-5">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Importação XML</h1>
          <p className="text-sm text-neutral-600 dark:text-neutral-400">Documentos e eventos fiscais por arquivo ou pasta local, sem consulta SEFAZ.</p>
        </div>
      </header>

      {erro && <div className="rounded-md border border-rose-200 bg-rose-50 text-rose-800 px-3 py-2 text-sm dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-200">{erro}</div>}

      <section className="card-surface p-4 space-y-4">
        <div className="flex items-center gap-2">
          <FileUp className="h-4 w-4 text-emerald-700 dark:text-emerald-300" />
          <h2 className="font-semibold text-sm">Upload de XML fiscal</h2>
        </div>
        <input
          className="field-input"
          type="file"
          accept=".xml,application/xml,text/xml"
          multiple
          onChange={(e) => setFiles(Array.from(e.target.files || []))}
        />
        <div className="flex flex-wrap gap-2">
          <button className="btn-secondary" type="button" disabled={busy || files.length === 0} onClick={() => uploadMut.mutate(true)}>
            <Search className="h-4 w-4" /> Prévia
          </button>
          <button className="btn-primary" type="button" disabled={busy || files.length === 0} onClick={() => uploadMut.mutate(false)}>
            <Play className="h-4 w-4" /> Importar
          </button>
        </div>
      </section>

      <section className="card-surface p-4 space-y-4">
        <div className="flex items-center gap-2">
          <FolderSearch className="h-4 w-4 text-sky-700 dark:text-sky-300" />
          <h2 className="font-semibold text-sm">Pasta no servidor</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <label className="md:col-span-3">
            <span className="field-label">Caminho</span>
            <input className="field-input" value={pasta} onChange={(e) => setPasta(e.target.value)} />
          </label>
          <label>
            <span className="field-label">Limite</span>
            <input className="field-input" type="number" value={limite} onChange={(e) => setLimite(Number(e.target.value) || 200)} />
          </label>
        </div>
        <div className="flex flex-wrap gap-2">
          <button className="btn-secondary" type="button" disabled={busy || !pasta.trim()} onClick={() => pastaMut.mutate(true)}>
            <Search className="h-4 w-4" /> Prévia
          </button>
          <button className="btn-primary" type="button" disabled={busy || !pasta.trim()} onClick={() => pastaMut.mutate(false)}>
            <Play className="h-4 w-4" /> Importar pasta
          </button>
        </div>
      </section>

      {busy && <LoadingSpinner />}
      <Resumo data={resultado} />
    </div>
  );
}
